import { ApiError } from "@/api/client";

export type AttemptStore = {
  keyFor(batchId: string, serializedBody: string): string;
  forget(batchId: string): void;
};

type Attempt = { key: string; body: string };

export function attemptStore(prefix: string): AttemptStore {
  const storageKey = (batchId: string) => `${prefix}.${batchId}`;
  // Used when sessionStorage is blocked, so retries on the page still share a key.
  const inMemory = new Map<string, Attempt>();

  function stored(batchId: string): Attempt | null {
    try {
      const raw = sessionStorage.getItem(storageKey(batchId));
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof (parsed as Attempt).key === "string" &&
        typeof (parsed as Attempt).body === "string"
      ) {
        return parsed as Attempt;
      }
      return null;
    } catch {
      return inMemory.get(batchId) ?? null;
    }
  }

  return {
    // The unfinished attempt's key when the request is the same, else a new one; survives a reload.
    keyFor(batchId, serializedBody) {
      const prior = stored(batchId);
      if (prior?.body === serializedBody) return prior.key;
      const attempt: Attempt = { key: crypto.randomUUID(), body: serializedBody };
      inMemory.set(batchId, attempt);
      try {
        // Stored before sending, so a reload while the answer is outstanding resends the same key.
        sessionStorage.setItem(storageKey(batchId), JSON.stringify(attempt));
      } catch {
        // The in-memory copy covers retries until the page is left.
      }
      return attempt.key;
    },
    forget(batchId) {
      inMemory.delete(batchId);
      try {
        sessionStorage.removeItem(storageKey(batchId));
      } catch {
        // Nothing stored, nothing to forget.
      }
    },
  };
}

export const emailAttempts = attemptStore("cipm.email-attempt");

// These are answered before the server looks the key up, so an earlier attempt with it may have committed.
export const BEFORE_KEY_CHECK = [401, 403, 408, 429];

/** A 4xx the server gave after checking the key: this attempt surely created nothing, so its key can go. */
export const answeredAfterKeyCheck = (error: unknown) =>
  error instanceof ApiError && error.status >= 400 && error.status < 500 && !BEFORE_KEY_CHECK.includes(error.status);
